import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../../i18n/useP';
import { DepartDuPlanSection, SoldeTresorerieSection } from './SoldesTab';
import IndirectTab from './IndirectTab';
import OpeningPositionsStep from './OpeningPositionsStep';
import './StartingStepper.css';

// Point de départ = suite d'étapes numérotées (chiffre encerclé), chacune
// dépliable/repliable (accordéon — « groupables »). Absorbe : départ du plan,
// durée de projection (ex-Graphique), solde de trésorerie, charges indirectes
// (ex-onglet dédié), et le choix du mode de planification personnel.

function Step({ n, title, subtitle, open, onToggle, children }) {
  const { t } = useTranslation();
  return (
    <section className={`stp-step${open ? ' is-open' : ''}`}>
      <button type="button" className="stp-step__head" onClick={onToggle} aria-expanded={open}>
        <span className="stp-step__num">{n}</span>
        <span className="stp-step__titles">
          <span className="stp-step__title">{title}</span>
          {subtitle && <span className="stp-step__sub">{subtitle}</span>}
        </span>
        <span className="stp-step__chev" aria-hidden>{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="stp-step__body">{children}</div>}
    </section>
  );
}

export default function StartingStepper({
  // Départ + solde
  resolved, scenario, periodLabel, startMonth, yearOptions, lastClosed, locked, onPatch,
  // Durée de projection
  horizon, onHorizonChange,
  // Charges indirectes
  tenantId, indirectCosts, onChangeIndirect,
  // Postes ouverts (manuels)
  openingManual, onOpeningChange,
  // Planification personnel
  personnelMode, onPersonnelModeChange,
}) {
  // Accordéon exclusif : toutes repliées par défaut, une seule ouverte à la fois.
  const { t } = useTranslation();
  const p = useP();
  const [openStep, setOpenStep] = useState(null);
  const open = (n) => openStep === n;
  const toggle = (n) => setOpenStep((cur) => (cur === n ? null : n));

  const H = Math.min(12, Math.max(1, Number(horizon) || 12));

  return (
    <div className="stp-wrap">
      <Step n={1} title={t('phrases:p_8f0bdf84e8')} open={open(1)} onToggle={() => toggle(1)}>
        <DepartDuPlanSection
          scenario={scenario}
          periodLabel={periodLabel}
          startMonth={startMonth}
          yearOptions={yearOptions}
          lastClosed={lastClosed}
          locked={locked}
          onPatch={onPatch}
        />
      </Step>

      <Step n={2} title={t('phrases:p_278f072906')} subtitle={`${H} mois`} open={open(2)} onToggle={() => toggle(2)}>
        <div className="stp-horizon">
          <span className="stp-horizon__label">{t('phrases:p_278f072906')}</span>
          <div className="stp-horizon__opts">
            <input
              type="range"
              min="1"
              max="12"
              step="1"
              className="stp-horizon__slider"
              value={H}
              disabled={locked}
              onChange={(e) => onHorizonChange(Math.min(12, Math.max(1, Math.round(Number(e.target.value) || 1))))}
              aria-label={t('phrases:p_6050b9bd69')}
            />
            <span className="stp-horizon__value">{H}</span>
            <span className="stp-horizon__unit">{t('phrases:p_339528c893')}</span>
          </div>
        </div>
      </Step>

      <Step n={3} title={t('phrases:p_2eb9bf52dd')} open={open(3)} onToggle={() => toggle(3)}>
        <SoldeTresorerieSection
          resolved={resolved}
          scenario={scenario}
          periodLabel={periodLabel}
          locked={locked}
          onPatch={onPatch}
        />
      </Step>

      <Step n={4} title={p('Postes ouverts')} open={open(4)} onToggle={() => toggle(4)}>
        <OpeningPositionsStep scenario={scenario} locked={locked} rows={openingManual} onChange={onOpeningChange} />
      </Step>

      <Step n={5} title={p('Charges indirectes')} open={open(5)} onToggle={() => toggle(5)}>
        <IndirectTab
          tenantId={tenantId}
          scenario={scenario}
          indirectCosts={indirectCosts}
          onChange={onChangeIndirect}
        />
      </Step>

      <Step
        n={6}
        title={p('Planification personnel')}
        subtitle={personnelMode === 'manual' ? p('Manuel') : p('Connecté à People')}
        open={open(6)}
        onToggle={() => toggle(6)}
      >
        <div className="stp-personnel">
          <p className="stp-personnel__hint"
            dangerouslySetInnerHTML={{ __html: p("Source des charges de personnel du plan. L'onglet <b>Charges personnel</b> s'adapte au mode choisi.") }}
          />
          <label className={`stp-radio${personnelMode !== 'manual' ? ' is-active' : ''}`}>
            <input
              type="radio"
              name="personnel-mode"
              checked={personnelMode !== 'manual'}
              disabled={locked}
              onChange={() => onPersonnelModeChange('people')}
            />
            <span>
              <b>{t('phrases:p_cfb504bbbd')}</b> {p('— planification personnel (charges dérivées des contrats / plan RH, recopiées sur les 12 mois à venir).')}
            </span>
          </label>
          <label className={`stp-radio${personnelMode === 'manual' ? ' is-active' : ''}`}>
            <input
              type="radio"
              name="personnel-mode"
              checked={personnelMode === 'manual'}
              disabled={locked}
              onChange={() => onPersonnelModeChange('manual')}
            />
            <span
              dangerouslySetInnerHTML={{ __html: p('<b>Manuel</b> — saisie directe : salaires nets mensuels, variation par mois, échéancier des charges sociales et taux.') }}
            />
          </label>
        </div>
      </Step>
    </div>
  );
}
